import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import type { FC } from 'react';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useResponsive } from 'contexts/ResponsiveContext';
import { useTickets } from 'contexts/TicketsContext';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import {
  AgeFilterValuesE,
  ScheduleFilterValuesE,
  StatusFilterValuesE,
  TicketsRefinementKeysE,
} from 'declarations/enums/tickets';
import type { TicketRequestCategoryI } from 'declarations/interfaces/ticketRequestCategories';
import { useFetchTicketRequestCategories } from 'hooks/api/useTicketRequestCategories';
import {
  useFetchTicketRequestSubcategories,
  useFetchTicketRequestSubcategoriesByCategoryIds,
} from 'hooks/api/useTicketSubcategories';
import useScheduleReport from 'pages/TicketsPage/components/ScheduleCards/hooks/useScheduleCards';

import useTicketsRefinement from '../TicketsRefinement/hooks/useTicketsRefinement';

import { TicketsFilterChips } from './TicketsFilterChips';
import { TicketsFiltersDropdown } from './TicketsFiltersDropdown';
import type { TicketFilterChipT } from './ticketsFiltersModel';

export type TicketsFiltersBarMobileLayoutProps = {
  filterButtons: React.ReactNode;
  filterChips: React.ReactNode;
};

export type TicketsFiltersBarProps = {
  renderMobileLayout?: (props: TicketsFiltersBarMobileLayoutProps) => React.ReactNode;
};

const ALL_ASSIGNEE = 'All';

export const TicketsFiltersBar: FC<TicketsFiltersBarProps> = ({
  renderMobileLayout,
}): JSX.Element => {
  const { isMobile } = useResponsive();
  const {
    activeTab,
    getSearchParam,
    roofTopsList,
    selectedRoofTop,
    updateSearchParamsArray,
    isScheduleTicketList,
    setIsScheduleTicketList,
    clearSearchParam,
  } = useTickets();

  const {
    isAssignedToMeFilterActive,
    isCloseStatusFilterActive,
    isOpenStatusFilterActive,
    handleClearAllFiltersClick,
    handleUpdateFilterClick,
    filtersReset,
  } = useTicketsRefinement();

  const categoriesResult = useFetchTicketRequestCategories(true, selectedRoofTop || undefined);

  const subCategoriesResult = useFetchTicketRequestSubcategories({
    isEnabled: activeTab === ActiveTabTypeE.Schedule,
    roofTopId: selectedRoofTop || undefined,
  });

  const selectedCategoryIdsForSubs = useMemo((): number[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.Category) || '';
    return raw
      .split(',')
      .filter(Boolean)
      .map((val): number => Number(val))
      .filter((id): boolean => !isNaN(id));
  }, [getSearchParam]);

  const ticketSubCategoriesByCategoryResult = useFetchTicketRequestSubcategoriesByCategoryIds({
    isEnabled: activeTab === ActiveTabTypeE.Tickets && selectedCategoryIdsForSubs.length > 0,
    categoryIds: selectedCategoryIdsForSubs,
    roofTopId: selectedRoofTop || undefined,
  });

  const { scheduleReport } = useScheduleReport();

  const adjustmentSubCategoryIds = useMemo((): string[] => {
    const fromTotal =
      scheduleReport?.totalAdjustedDataBySubCategories?.map((s): string =>
        String(s.subCategoryId)
      ) ?? [];
    if (fromTotal.length > 0) return fromTotal;
    const firstWithData = scheduleReport?.data?.find(
      d => d.adjustedDataBySubCategories && d.adjustedDataBySubCategories.length > 0
    );
    return (
      firstWithData?.adjustedDataBySubCategories?.map((s): string => String(s.subCategoryId)) ?? []
    );
  }, [scheduleReport]);

  const idealSubCategoryIds = useMemo((): string[] => {
    const allIds =
      scheduleReport?.totalDataBySubCategories?.map((s): string => String(s.subCategoryId)) ?? [];
    const adjustmentSet = new Set(adjustmentSubCategoryIds);
    return allIds.filter((id): boolean => !adjustmentSet.has(id));
  }, [scheduleReport, adjustmentSubCategoryIds]);

  const isAdjustmentsView = useMemo((): boolean => {
    if (adjustmentSubCategoryIds.length === 0) return false;
    const subParam = getSearchParam(TicketsRefinementKeysE.SubCategory) || '';
    const subIds = subParam ? subParam.split(',').map((s): string => s.trim()) : [];
    return (
      subIds.length === adjustmentSubCategoryIds.length &&
      adjustmentSubCategoryIds.every((id): boolean => subIds.includes(id))
    );
  }, [adjustmentSubCategoryIds, getSearchParam]);

  const isIdealView = useMemo((): boolean => {
    if (idealSubCategoryIds.length === 0) return false;
    const subParam = getSearchParam(TicketsRefinementKeysE.SubCategory) || '';
    const subIds = subParam ? subParam.split(',').map((s): string => s.trim()) : [];
    return (
      subIds.length === idealSubCategoryIds.length &&
      idealSubCategoryIds.every((id): boolean => subIds.includes(id))
    );
  }, [idealSubCategoryIds, getSearchParam]);

  const scheduleCategoryId = useMemo((): number | null => {
    const scheduleCategory = categoriesResult.data?.find(
      (item: TicketRequestCategoryI): boolean => item.categoryName.toLowerCase() === 'schedule'
    );
    return scheduleCategory ? scheduleCategory.id : null;
  }, [categoriesResult.data]);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleToggleDropdown = useCallback((): void => {
    setDropdownOpen((prev: boolean): boolean => !prev);
  }, []);

  const handleCloseDropdown = useCallback((): void => {
    setDropdownOpen(false);
  }, []);

  // --- Parsed URL params ---

  const selectedCategoryIds = useMemo((): number[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.Category) || '';
    return raw
      .split(',')
      .filter(Boolean)
      .map((val): number => Number(val))
      .filter((id): boolean => !isNaN(id));
  }, [getSearchParam]);

  const selectedSubCategoryIds = useMemo((): number[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.SubCategory) || '';
    return raw
      .split(',')
      .filter(Boolean)
      .map((val): number => Number(val))
      .filter((id): boolean => !isNaN(id));
  }, [getSearchParam]);

  const selectedRequestSubCategoryIds = useMemo((): number[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.RequestSubCategoryIds) || '';
    return raw
      .split(',')
      .filter(Boolean)
      .map((val): number => Number(val))
      .filter((id): boolean => !isNaN(id));
  }, [getSearchParam]);

  const selectedAssigneeNames = useMemo((): string[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.Assignee) || '';
    return raw.split(',').filter(Boolean);
  }, [getSearchParam]);

  const selectedScheduleAgeValues = useMemo((): string[] => {
    const raw = getSearchParam(TicketsRefinementKeysE.Schedule) || '';
    return raw
      .split(',')
      .map(v => v.trim())
      .filter(Boolean);
  }, [getSearchParam]);

  const selectedDealerId = useMemo((): string | null => {
    const raw = getSearchParam(TicketsRefinementKeysE.RoofTopId) || '';
    return raw || null;
  }, [getSearchParam]);

  const scheduleAgeValueToLabel: Record<string, string> = useMemo(
    () => ({
      [ScheduleFilterValuesE.LessThan31Days]: '0-30 days',
      [ScheduleFilterValuesE.From31To60Days]: '31-60 days',
      [ScheduleFilterValuesE.From61To90Days]: '61-90 days',
      [ScheduleFilterValuesE.From91To120Days]: '91-120 days',
      [ScheduleFilterValuesE.Over120Days]: '121+ days',
    }),
    []
  );

  // --- Chip model ---

  const chips = useMemo((): TicketFilterChipT[] => {
    const result: TicketFilterChipT[] = [];

    // Pinned chips (always shown, not deletable)
    result.push({
      key: 'my-work',
      label: 'My Work',
      toggled: isAssignedToMeFilterActive,
      deletable: false,
      onClick: handleUpdateFilterClick(TicketsRefinementKeysE.AssignedToMe, 'true'),
    });

    result.push({
      key: 'open',
      label: 'Open',
      toggled: isOpenStatusFilterActive,
      deletable: false,
      onClick: handleUpdateFilterClick(TicketsRefinementKeysE.Status, StatusFilterValuesE.Open),
    });

    result.push({
      key: 'closed',
      label: 'Closed',
      toggled: isCloseStatusFilterActive,
      deletable: false,
      onClick: handleUpdateFilterClick(TicketsRefinementKeysE.Status, StatusFilterValuesE.Closed),
    });

    // Ticket Age
    const ticketAge = getSearchParam(TicketsRefinementKeysE.TicketAge);
    if (ticketAge === AgeFilterValuesE.Under24Hours) {
      result.push({
        key: 'age-under24',
        label: 'Under 24h',
        toggled: true,
        deletable: true,
        onClick: handleUpdateFilterClick(
          TicketsRefinementKeysE.TicketAge,
          AgeFilterValuesE.Under24Hours
        ),
        onDelete: handleUpdateFilterClick(
          TicketsRefinementKeysE.TicketAge,
          AgeFilterValuesE.Under24Hours
        ),
      });
    }
    if (ticketAge === AgeFilterValuesE.Over24Hours) {
      result.push({
        key: 'age-over24',
        label: 'Over 24h',
        toggled: true,
        deletable: true,
        onClick: handleUpdateFilterClick(
          TicketsRefinementKeysE.TicketAge,
          AgeFilterValuesE.Over24Hours
        ),
        onDelete: handleUpdateFilterClick(
          TicketsRefinementKeysE.TicketAge,
          AgeFilterValuesE.Over24Hours
        ),
      });
    }

    // Past 30 days
    if (getSearchParam(TicketsRefinementKeysE.Past30Days) === 'true') {
      result.push({
        key: 'past-30-days',
        label: 'Past 30 Days',
        toggled: true,
        deletable: true,
        onClick: handleUpdateFilterClick(TicketsRefinementKeysE.Past30Days, 'true'),
        onDelete: handleUpdateFilterClick(TicketsRefinementKeysE.Past30Days, 'true'),
      });
    }

    // Past month
    if (getSearchParam(TicketsRefinementKeysE.PastMonth) === 'true') {
      result.push({
        key: 'past-month',
        label: 'Past Month',
        toggled: true,
        deletable: true,
        onClick: handleUpdateFilterClick(TicketsRefinementKeysE.PastMonth, 'true'),
        onDelete: handleUpdateFilterClick(TicketsRefinementKeysE.PastMonth, 'true'),
      });
    }

    // Archived
    if (getSearchParam(TicketsRefinementKeysE.IsArchived) === 'true') {
      result.push({
        key: 'archived',
        label: 'Archived',
        toggled: true,
        deletable: true,
        onClick: handleUpdateFilterClick(TicketsRefinementKeysE.IsArchived, 'true'),
        onDelete: handleUpdateFilterClick(TicketsRefinementKeysE.IsArchived, 'true'),
      });
    }

    // Assignee
    selectedAssigneeNames
      .filter((name): boolean => name !== ALL_ASSIGNEE)
      .forEach((name): void => {
        result.push({
          key: `assignee-${name}`,
          label: name,
          toggled: true,
          deletable: true,
          onClick: (): void => {
            const next = selectedAssigneeNames.filter((n): boolean => n !== name);
            updateSearchParamsArray(TicketsRefinementKeysE.Assignee, next);
          },
          onDelete: (): void => {
            const next = selectedAssigneeNames.filter((n): boolean => n !== name);
            updateSearchParamsArray(TicketsRefinementKeysE.Assignee, next);
          },
        });
      });

    // Request subcategories (Tickets tab only)
    if (activeTab !== ActiveTabTypeE.Schedule) {
      selectedRequestSubCategoryIds.forEach((id): void => {
        const name =
          ticketSubCategoriesByCategoryResult.data?.find((s): boolean => s.id === id)?.name ||
          `Subcategory ${id}`;
        result.push({
          key: `request-subcat-${id}`,
          label: name,
          toggled: true,
          deletable: true,
          onClick: (): void => {
            const next = selectedRequestSubCategoryIds.filter((v): boolean => v !== id);
            updateSearchParamsArray(
              TicketsRefinementKeysE.RequestSubCategoryIds,
              next.length ? next : null
            );
          },
          onDelete: (): void => {
            const next = selectedRequestSubCategoryIds.filter((v): boolean => v !== id);
            updateSearchParamsArray(
              TicketsRefinementKeysE.RequestSubCategoryIds,
              next.length ? next : null
            );
          },
        });
      });
    }

    // Categories (hidden on Schedule tab)
    activeTab !== ActiveTabTypeE.Schedule &&
      selectedCategoryIds
        .filter((id): boolean => (scheduleCategoryId ? id !== scheduleCategoryId : true))
        .forEach((id): void => {
          const name =
            categoriesResult.data?.find((c): boolean => c.id === id)?.categoryName ||
            `Category ${id}`;
          result.push({
            key: `category-${id}`,
            label: name,
            toggled: true,
            deletable: true,
            onClick: (): void => {
              const next = selectedCategoryIds.filter((v): boolean => v !== id);
              updateSearchParamsArray(TicketsRefinementKeysE.Category, next);
            },
            onDelete: (): void => {
              const next = selectedCategoryIds.filter((v): boolean => v !== id);
              updateSearchParamsArray(TicketsRefinementKeysE.Category, next);
            },
          });
        });

    const DEALER_DEPENDENT_FILTER_KEYS: TicketsRefinementKeysE[] = [
      TicketsRefinementKeysE.RoofTopId,
      TicketsRefinementKeysE.Category,
      TicketsRefinementKeysE.SubCategory,
      TicketsRefinementKeysE.RequestSubCategoryIds,
    ];

    // Dealer (single selection)
    if (selectedDealerId) {
      const dealerName =
        roofTopsList?.find(r => r.id === selectedDealerId)?.name || `Dealer ${selectedDealerId}`;
      const handleClearDealer = (): void => {
        clearSearchParam(DEALER_DEPENDENT_FILTER_KEYS);
      };
      result.push({
        key: `dealer-${selectedDealerId}`,
        label: dealerName,
        toggled: true,
        deletable: true,
        onClick: handleClearDealer,
        onDelete: handleClearDealer,
      });
    } else {
      result.push({
        key: 'dealer-all',
        label: 'All Dealers',
        toggled: true,
        deletable: false,
        onClick: (): void => undefined,
      });
    }

    // Schedule file filter chip
    if (
      activeTab === ActiveTabTypeE.Schedule &&
      isScheduleTicketList &&
      getSearchParam(TicketsRefinementKeysE.ScheduleFileId)
    ) {
      const handleRemoveScheduleFileFilter = (): void => {
        clearSearchParam(TicketsRefinementKeysE.ScheduleFileId);
        setIsScheduleTicketList(false);
      };
      result.push({
        key: `schedule-file`,
        label: 'Schedule file',
        toggled: true,
        deletable: true,
        onClick: handleRemoveScheduleFileFilter,
        onDelete: handleRemoveScheduleFileFilter,
      });
    }

    // Schedule age range chips
    if (activeTab === ActiveTabTypeE.Schedule) {
      selectedScheduleAgeValues.forEach((value): void => {
        const baseLabel = scheduleAgeValueToLabel[value] ?? value;
        let label = baseLabel;
        if (isIdealView) label = `Adjusted: ${baseLabel}`;
        else if (isAdjustmentsView) label = `Adjustments: ${baseLabel}`;
        let viewKey = 'sched';
        if (isIdealView) viewKey = 'adjusted';
        else if (isAdjustmentsView) viewKey = 'adj';
        const shouldClearSubOnEmpty = isAdjustmentsView || isIdealView;
        result.push({
          key: `schedule-age-${value}-${viewKey}`,
          label,
          toggled: true,
          deletable: true,
          onClick: (): void => {
            const next = selectedScheduleAgeValues.filter((v): boolean => v !== value);
            if (next.length === 0 && shouldClearSubOnEmpty) {
              updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, null);
            }
            updateSearchParamsArray(TicketsRefinementKeysE.Schedule, next.length > 0 ? next : null);
          },
          onDelete: (): void => {
            const next = selectedScheduleAgeValues.filter((v): boolean => v !== value);
            if (next.length === 0 && shouldClearSubOnEmpty) {
              updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, null);
            }
            updateSearchParamsArray(TicketsRefinementKeysE.Schedule, next.length > 0 ? next : null);
          },
        });
      });

      // Schedule subcategory chips
      selectedSubCategoryIds.forEach((id): void => {
        const name =
          subCategoriesResult.data?.find((s): boolean => s.id === id)?.name || `Subcategory ${id}`;
        result.push({
          key: `subcat-${id}`,
          label: name,
          toggled: true,
          deletable: true,
          onClick: (): void => {
            const next = selectedSubCategoryIds.filter((v): boolean => v !== id);
            updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, next.length ? next : null);
          },
          onDelete: (): void => {
            const next = selectedSubCategoryIds.filter((v): boolean => v !== id);
            updateSearchParamsArray(TicketsRefinementKeysE.SubCategory, next.length ? next : null);
          },
        });
      });
    }

    return result;
  }, [
    activeTab,
    categoriesResult.data,
    clearSearchParam,
    getSearchParam,
    isAdjustmentsView,
    isIdealView,
    handleUpdateFilterClick,
    isAssignedToMeFilterActive,
    isCloseStatusFilterActive,
    isOpenStatusFilterActive,
    isScheduleTicketList,
    roofTopsList,
    scheduleAgeValueToLabel,
    scheduleCategoryId,
    selectedAssigneeNames,
    selectedCategoryIds,
    selectedDealerId,
    selectedRequestSubCategoryIds,
    selectedScheduleAgeValues,
    selectedSubCategoryIds,
    setIsScheduleTicketList,
    subCategoriesResult.data,
    ticketSubCategoriesByCategoryResult.data,
    updateSearchParamsArray,
  ]);

  const activeNonDefaultFilterCount = useMemo((): number => {
    return chips.filter((c): boolean => c.deletable && c.toggled).length;
  }, [chips]);

  const handleClear = useCallback((): void => {
    handleClearAllFiltersClick();
    handleCloseDropdown();
  }, [handleClearAllFiltersClick, handleCloseDropdown]);

  // --- Layout elements ---

  const filterButtons = (
    <>
      <Tooltip title="Filters" arrow>
        <IconButton
          ref={anchorRef}
          aria-label="Filters"
          size="small"
          onClick={handleToggleDropdown}
        >
          <Badge badgeContent={activeNonDefaultFilterCount} color="primary">
            <FilterListIcon sx={{ color: (theme): string => theme.palette.primary.main }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Tooltip title="Clear filters" arrow>
        <IconButton aria-label="Clear filters" size="small" onClick={handleClear}>
          <FilterListOffIcon sx={{ color: (theme): string => theme.palette.primary.main }} />
        </IconButton>
      </Tooltip>
    </>
  );

  const filterChips = (
    <Box
      sx={{
        ...(isMobile ? { width: '100%' } : { ml: 1, flex: 1 }),
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <TicketsFilterChips chips={chips} />
    </Box>
  );

  // --- Render ---

  if (isMobile && renderMobileLayout) {
    return (
      <>
        {renderMobileLayout({ filterButtons, filterChips })}
        <TicketsFiltersDropdown
          anchorEl={anchorRef.current}
          isOpen={dropdownOpen}
          onClose={handleCloseDropdown}
          onToggleFilter={handleUpdateFilterClick}
          shouldFiltersReset={filtersReset}
        />
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%', minWidth: 0 }}>
      {filterButtons}
      {filterChips}
      <TicketsFiltersDropdown
        anchorEl={anchorRef.current}
        isOpen={dropdownOpen}
        onClose={handleCloseDropdown}
        onToggleFilter={handleUpdateFilterClick}
        shouldFiltersReset={filtersReset}
      />
    </Box>
  );
};
